import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isAccentColor, isAppearance } from "../theme/themeConfig";
import { readThemePreferences, writeThemePreferences } from "../theme/themeStorage";
import {
  applyThemeToDocument,
  resolveAppearance,
  systemPrefersDark,
  watchSystemAppearance,
} from "../theme/themeDom";

const ThemeContext = createContext(null);

// Owns the appearance/accent state. Applying the theme only changes attributes
// on <html>, so nothing below the provider remounts when it changes.
export function ThemeProvider({ children }) {
  const [preferences, setPreferences] = useState(readThemePreferences);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

  useEffect(() => watchSystemAppearance(setPrefersDark), []);

  const resolvedAppearance = resolveAppearance(preferences.appearance, prefersDark);

  // Layout effect so the attributes change in the same frame as the render.
  useLayoutEffect(() => {
    applyThemeToDocument(resolvedAppearance, preferences.accentColor);
  }, [resolvedAppearance, preferences.accentColor]);

  // Persist only once the user has changed something; the initial value was
  // just read from storage (or is the default).
  const loadedPreferences = useRef(preferences);
  useEffect(() => {
    if (preferences === loadedPreferences.current) return;
    writeThemePreferences(preferences);
  }, [preferences]);

  const setAppearance = useCallback((appearance) => {
    if (!isAppearance(appearance)) return;
    setPreferences((prev) =>
      prev.appearance === appearance ? prev : { ...prev, appearance },
    );
  }, []);

  const setAccentColor = useCallback((accentColor) => {
    if (!isAccentColor(accentColor)) return;
    setPreferences((prev) =>
      prev.accentColor === accentColor ? prev : { ...prev, accentColor },
    );
  }, []);

  const value = useMemo(
    () => ({
      appearance: preferences.appearance,
      accentColor: preferences.accentColor,
      resolvedAppearance,
      setAppearance,
      setAccentColor,
    }),
    [preferences, resolvedAppearance, setAppearance, setAccentColor],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used inside a ThemeProvider");
  }
  return context;
}
